import React, { useEffect, useMemo, useState } from "react";
import { Outlet, Link } from "react-router-dom";
import {
  MessageCircle,
  Phone,
  Mail,
  MapPin,
  ShieldCheck,
  Truck,
  CreditCard,
  Instagram,
  Facebook,
  Youtube,
  User,
} from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import HeaderPublic from "../HeaderPublic/HeaderPublic";
import logo from "../../../assets/Images/portakal11.png";
import { postRequest } from "../../../api/postRequest";

type ApiResponse<T> = {
  success: boolean;
  errorCode?: string;
  errorDetail?: string;
  data?: T;
};

type FooterLinkItem = { label: string; to: string };
type SocialLinkItem = { platform: string; label: string; href: string };
type TrustBadgeItem = { icon: string; title: string; desc: string };

type HomeConfig = {
  brand?: { logoUrl?: string; tagline?: string };
  footer?: {
    companyName?: string;
    companyTagline?: string;
    description?: string;
    whatsappNumber?: string;
    whatsappButtonText?: string;
    phoneDisplay?: string;
    phoneHref?: string;
    mailDisplay?: string;
    mailHref?: string;
    addressLine?: string;
    contactPerson?: string;
    socialLinks?: SocialLinkItem[];
    trustBadges?: TrustBadgeItem[];
    bottomLinks?: FooterLinkItem[];
  };
};

const defaultFooterConfig = {
  companyName: "PORTAKAL3D",
  companyTagline: "3D Yazici & Filament",
  description:
    "3D yazicilar, filamentler ve yedek parcalarda dogru urunu hizlica bulmaniz icin sade, guvenilir ve mobil uyumlu bir alisveris deneyimi sunariz.",
  whatsappNumber: "905432654524",
  whatsappButtonText: "WhatsApp'tan Yaz",
  phoneDisplay: "+90 543 265 45 24",
  phoneHref: "tel:+905432654524",
  mailDisplay: "portakal3d@hotmail.com",
  mailHref: "mailto:portakal3d@hotmail.com",
  addressLine: "Elif Su Uludag Mh. 264 Sk. No:7 Aydindag Apartmani Kat:3 Daire:9 Saricam / Adana",
  contactPerson: "Muzaffer Surucu",
  socialLinks: [
    { platform: "Instagram", label: "Instagram", href: "https://www.instagram.com/portakal3d/" },
  ] as SocialLinkItem[],
  trustBadges: [
    { icon: "Truck", title: "Hizli Kargo", desc: "Stoklu urunlerde ayni gun" },
    { icon: "ShieldCheck", title: "Guvenli Alisveris", desc: "SSL ve guvenli odeme" },
    { icon: "CreditCard", title: "Odeme Secenekleri", desc: "K.Karti / Havale" },
    { icon: "Phone", title: "Teknik Destek", desc: "Hizli donus" },
  ] as TrustBadgeItem[],
  bottomLinks: [
    { label: "KVKK", to: "/kvkk" },
    { label: "Gizlilik", to: "/gizlilik" },
    { label: "Mesafeli Satis Sozlesmesi", to: "/mesafeli-satis" },
    { label: "Cerez Politikasi", to: "/cerez" },
  ] as FooterLinkItem[],
};

const footerIconMap: Record<string, React.ReactNode> = {
  Truck: <Truck size={18} />,
  ShieldCheck: <ShieldCheck size={18} />,
  CreditCard: <CreditCard size={18} />,
  Phone: <Phone size={18} />,
};

const socialIconMap: Record<string, React.ReactNode> = {
  Instagram: <Instagram size={18} />,
  Facebook: <Facebook size={18} />,
  Youtube: <Youtube size={18} />,
};

const hexToRgb = (hex: string) => {
  const h = (hex || "#0f1115").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

const useSurfaces = (theme?: any) => {
  const bg = theme?.backgroundColor || "#F8FAFC";
  const isDark = luminance(hexToRgb(bg)) < 0.5;

  return {
    bg,
    surface1: isDark ? "rgba(255,255,255,.04)" : "#ffffff",
    surface2: isDark ? "rgba(255,255,255,.06)" : "#f1f5f9",
    divider: isDark ? "rgba(255,255,255,.10)" : "#e5e7eb",
    textPrimary: theme?.textPrimary ?? (isDark ? "#ffffff" : "#0f172a"),
    textSecondary: theme?.textSecondary ?? (isDark ? "rgba(255,255,255,.72)" : "#64748b"),
    primary: "#2563EB",
    primaryHover: "#1D4ED8",
    whatsapp: "#25D366",
    whatsappHover: "#1DA851",
  };
};

const getFooterConfig = (config: HomeConfig | null) => {
  try {
    const rawFooter: NonNullable<HomeConfig["footer"]> = (config?.footer ?? {}) as NonNullable<HomeConfig["footer"]>;
    return {
      ...defaultFooterConfig,
      ...(rawFooter ?? {}),
      socialLinks:
        Array.isArray(rawFooter?.socialLinks) && rawFooter.socialLinks.length > 0
          ? rawFooter.socialLinks
          : defaultFooterConfig.socialLinks,
      trustBadges:
        Array.isArray(rawFooter?.trustBadges) && rawFooter.trustBadges.length > 0
          ? rawFooter.trustBadges
          : defaultFooterConfig.trustBadges,
      bottomLinks:
        Array.isArray(rawFooter?.bottomLinks) && rawFooter.bottomLinks.length > 0
          ? rawFooter.bottomLinks
          : defaultFooterConfig.bottomLinks,
    };
  } catch (err) {
    console.error("getFooterConfig error:", err);
    return defaultFooterConfig;
  }
};

const PublicLayout: React.FC = () => {
  const { theme } = useTheme();
  const surfaces = useSurfaces(theme);
  const [config, setConfig] = useState<HomeConfig | null>(null);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await postRequest<{}, ApiResponse<Array<{ bilgiler: string | null }>>>(
          "/AnaEkranVerileri/listele",
          {}
        );
        const raw = res?.data?.[0]?.bilgiler;

        if (res?.success && raw) {
          setConfig(JSON.parse(raw) as HomeConfig);
        }
      } catch (err) {
        console.error("PublicLayout config load error:", err);
      }
    };

    loadConfig();
  }, []);

  const footerConfig = useMemo(() => getFooterConfig(config), [config]);
  const footerLogo = config?.brand?.logoUrl || logo;

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: surfaces.bg }}>
      <HeaderPublic />

      <main className="flex-1">
        <Outlet />
      </main>

      <a
        href={`https://wa.me/${footerConfig.whatsappNumber}`}
        target="_blank"
        rel="noopener noreferrer"
        className={[
          "fixed z-50 flex items-center gap-2 rounded-full",
          "p-3 sm:px-4 sm:py-2.5",
          "shadow-lg sm:shadow-xl text-white font-semibold",
          "transition-transform active:scale-95 sm:hover:scale-105",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70",
          "right-4 sm:right-6",
        ].join(" ")}
        style={{
          backgroundColor: surfaces.whatsapp,
          bottom: "calc(5rem + env(safe-area-inset-bottom, 0px))",
        }}
        aria-label="WhatsApp ile iletisime gecin"
        onMouseEnter={(e) => ((e.currentTarget.style.backgroundColor = surfaces.whatsappHover))}
        onMouseLeave={(e) => ((e.currentTarget.style.backgroundColor = surfaces.whatsapp))}
      >
        <MessageCircle size={22} className="text-white" />
        <span className="hidden sm:inline text-sm">{footerConfig.whatsappButtonText}</span>
      </a>

      <footer
        className="border-t"
        style={{ borderColor: surfaces.divider, background: surfaces.surface1, color: surfaces.textPrimary }}
      >
        <div className="border-b" style={{ borderColor: surfaces.divider, background: surfaces.surface2 }}>
          <div className="mx-auto max-w-7xl px-4 py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {footerConfig.trustBadges.map((badge, index) => (
                <TrustPill
                  key={`${badge.title}-${index}`}
                  icon={footerIconMap[badge.icon] ?? <ShieldCheck size={18} />}
                  title={badge.title}
                  desc={badge.desc}
                  textSecondary={surfaces.textSecondary}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 py-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4">
              <div className="flex items-center gap-3">
                <div
                  className="h-11 w-11 rounded-2xl  bg-white grid place-items-center "
                  style={{ borderColor: surfaces.divider }}
                >
                  <img src={footerLogo} alt={`${footerConfig.companyName} Logo`} className="rounded-2xl w-auto p-0 m-0" />
                </div>

                <div className="leading-tight">
                  <div className="text-lg font-normal tracking-tight">{footerConfig.companyName}</div>
                  <div className="text-sm" style={{ color: surfaces.textSecondary }}>
                    {footerConfig.companyTagline}
                  </div>
                </div>
              </div>

              <p className="mt-4 text-sm leading-relaxed" style={{ color: surfaces.textSecondary }}>
                {footerConfig.description}
              </p>

              <div className="mt-5 flex items-center gap-2">
                {footerConfig.socialLinks.map((item, index) => (
                  <SocialIcon
                    key={`${item.label}-${index}`}
                    href={item.href}
                    label={item.label}
                    icon={socialIconMap[item.platform] ?? <Instagram size={18} />}
                    color={surfaces.textSecondary}
                  />
                ))}
              </div>
            </div>

            <div className="md:col-span-2">
              <FooterTitle>Kurumsal</FooterTitle>
              <FooterLink to="/hakkimizda">Hakkimizda</FooterLink>
              <FooterLink to="/iletisim">Iletisim</FooterLink>
              <FooterLink to="/projeler">Projelerimiz</FooterLink>
              <FooterLink to="/mesafelisatissozlesmesi">Mesafeli Satis Sozlesmesi</FooterLink>
              <FooterLink to="/teslimatdegisimiadekosullari">Teslimat ve Iade</FooterLink>
              <FooterLink to="/gizlilikpolitikasi">Gizlilik Politikasi</FooterLink>
              <FooterLink to="/kullanicisozlesmesi">Kullanici Sozlesmesi</FooterLink>
              <FooterLink to="/sss">S.S.S</FooterLink>
            </div>

            <div className="md:col-span-3">
              <FooterTitle>Musteri</FooterTitle>
              <FooterLink to="/musteri/giris">Giris</FooterLink>
              <FooterLink to="/musteri/kayit">Kayit Ol</FooterLink>
              <FooterLink to="/musteri/panel">Hesabim</FooterLink>
            </div>

            <div className="md:col-span-3">
              <FooterTitle>Iletisim</FooterTitle>

              <div className="space-y-3">
                <InfoRow
                  icon={<Phone size={16} />}
                  text={footerConfig.phoneDisplay}
                  href={footerConfig.phoneHref}
                  secondary={surfaces.textSecondary}
                />

                <InfoRow
                  icon={<Mail size={16} />}
                  text={footerConfig.mailDisplay}
                  href={footerConfig.mailHref}
                  secondary={surfaces.textSecondary}
                />

                <InfoRow
                  icon={<MapPin size={16} />}
                  text={footerConfig.addressLine}
                  secondary={surfaces.textSecondary}
                />

                <div className="flex items-start gap-2">
                  <span className="mt-0.5 opacity-80">
                    <User size={16} />
                  </span>
                  <span className="text-sm" style={{ color: surfaces.textSecondary }}>
                    Yetkili: {footerConfig.contactPerson}
                  </span>
                </div>
              </div>

              <div className="mt-5">
                <a
                  href={`https://wa.me/${footerConfig.whatsappNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-teal-500 gap-2 h-11 px-4 rounded-xl text-sm font-semibold text-white transition"
                  onMouseEnter={(e) => (e.currentTarget.style.background = surfaces.primaryHover)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = surfaces.primary)}
                >
                  <MessageCircle size={18} />
                  {footerConfig.whatsappButtonText}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t" style={{ borderColor: surfaces.divider }}>
          <div className="mx-auto max-w-7xl px-4 py-4 flex flex-col md:flex-row gap-2 md:items-center md:justify-between">
            <div className="text-sm" style={{ color: surfaces.textSecondary }}>
              &copy; {new Date().getFullYear()} {footerConfig.companyName}. Tum haklari saklidir.
            </div>

            <div className="flex flex-wrap gap-3 text-sm">
              {footerConfig.bottomLinks.map((item, index) => (
                <Link key={`${item.label}-${index}`} to={item.to} className="hover:underline" style={{ color: surfaces.textSecondary }}>
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;

const FooterTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-sm font-normal tracking-tight mb-3">{children}</div>
);

const FooterLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (
  <Link
    to={to}
    className="block text-sm py-1.5 hover:underline"
    style={{ color: "rgb(var(--text-muted))" }}
  >
    {children}
  </Link>
);

const InfoRow: React.FC<{
  icon: React.ReactNode;
  text: string;
  href?: string;
  secondary: string;
}> = ({ icon, text, href, secondary }) => {
  const content = (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 opacity-80">{icon}</span>
      <span className="text-sm" style={{ color: secondary }}>
        {text}
      </span>
    </div>
  );

  if (!href) {
    return content;
  }

  return (
    <a href={href} className="block hover:underline">
      {content}
    </a>
  );
};

const TrustPill: React.FC<{
  icon: React.ReactNode;
  title: string;
  desc: string;
  textSecondary: string;
}> = ({ icon, title, desc, textSecondary }) => (
  <div
    className="rounded-2xl border bg-white/70 backdrop-blur px-4 py-3 flex items-start gap-3"
    style={{ borderColor: "rgb(var(--border))" }}
  >
    <div className="h-9 w-9 rounded-xl grid place-items-center bg-gradient-to-r from-blue-600 to-teal-500 text-white shrink-0">
      {icon}
    </div>
    <div className="min-w-0">
      <div className="text-sm font-normal leading-tight">{title}</div>
      <div className="text-xs mt-0.5" style={{ color: textSecondary }}>
        {desc}
      </div>
    </div>
  </div>
);

const SocialIcon: React.FC<{
  href: string;
  label: string;
  icon: React.ReactNode;
  color: string;
}> = ({ href, label, icon, color }) => (
  <a
    href={href}
    aria-label={label}
    className="h-10 w-10 rounded-xl border bg-white grid place-items-center hover:scale-[1.03] active:scale-[0.98] transition"
    style={{ borderColor: "rgb(var(--border))", color }}
    target="_blank"
    rel="noreferrer"
  >
    {icon}
  </a>
);
